import { Component, Event, EventEmitter, forceUpdate, h, Host, Prop, State, Watch } from "@stencil/core";
import { QuestionRadio } from "./question-radio";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import { Answers, questions, Question, firstQuestionKey, buildRequestData } from "../../api/proactive-case-closure";
import { QuestionComment } from "./question-comment";
import { request } from "../../utils/request";
import { createTicket } from "../../api/createTicket";
import { EnvConfig, getEnvConfig, setEnv } from "../../config/api";

const baseClass = "mf-proactive-case-closure";
/**
 * Micro-frontend component for the proactive case closure form
 * This form shows a series of questions to the user and collects the answers
 * depending on the answers the next question is displayed
 */
@Component({
    tag: "mf-proactive-case-closure",
    styleUrl: "proactive-case-closure.css",
    shadow: false
})
export class ProactiveCaseClosure {
    /** SIS case ID */
    @Prop() callId: string = "";
    /**
     * The locale to use for translations
     */
    @Prop({ reflect: true }) locale: string = "en-US";
    /**
     * Indicates if the component is used in an app context
     */
    @Prop() isApp: boolean = false;
    /**
     * Environment string to decide which endpoints to use
     */
    @Prop() env: string = "prod";

    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    @Prop() legacyApi: boolean = false;

    @State() questions: Question[] = [];
    @State() answers: Answers = {};
    /** indicates if all required answers were given */
    @State() complete: boolean = false;
    @State() submitting: boolean = false;
    @State() submitSuccess: boolean = false;
    @State() submitError: string | null = null;
    @State() authError: string | null = null;
    @State() config: EnvConfig | null = null;
    @State() loadingTranslations = true;
    @State() isAuthenticating = false;

    /**
     * the TBDB-Token is required for using the PublicAPI. It is obtained from /user/createTicket with an IDM-Token.
     */
    private tbdbToken: string | null = null;

    /**
     * Event that is fired when the user clicks the cancel button
     */
    @Event({
        eventName: "mfServiceCallCancel",
        bubbles: true,
        composed: true
    })
    cancelEvent: EventEmitter<void>;

    /**
     * Event that is fired when the user clicks the final OK button
     */
    @Event({
        eventName: "mfServiceCallSuccess",
        bubbles: true,
        composed: true
    })
    successEvent: EventEmitter<void>;

    /**
     * Event that is fired when the success message is shown
     * (this is used in the modal to trigger a reload after the modal is closed via X, only when the massage has been shown)
     */
    @Event({
        eventName: "mfServiceCallShowSuccess",
        bubbles: true,
        composed: true
    })
    showSuccessEvent: EventEmitter<void>;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        if (!this.isApp) {
            this.isAuthenticating = true;
        }
        this.setEnv();
        this.loadTranslations();
        this.updateQuestions();
        this.checkComplete();
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    @Watch("env")
    private setEnv() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
    }

    @Watch("submitSuccess")
    submitSuccessChanged() {
        if (this.submitSuccess) {
            this.showSuccessEvent.emit();
        }
    }

    private loadTranslations() {
        this.loadingTranslations = true;
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
            this.loadingTranslations = false;
        });
    }

    private submit() {
        const data = buildRequestData(this.answers, this.callId);
        if (this.isApp) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((window as any).SendRawMessage as any)({ type: "proactiveCaseClosure", data });
            return;
        }
        if (!this.tbdbToken) {
            this.submitError = "No TBDB token available";
            return;
        }

        this.submitting = true;
        request<unknown>("/servicecall/proactivecaseclosure", data, this.tbdbToken)
            .then((response) => {
                if (response) {
                    this.submitSuccess = true;
                } else {
                    this.submitError = "An error occurred while submitting the form";
                }
                this.submitting = false;
            })
            .catch((error) => {
                this.submitError = error || "Unknown error";
                this.submitting = false;
            });
    }

    private cancel() {
        this.cancelEvent.emit();
    }

    private finish() {
        this.successEvent.emit();
    }

    /**
     * this method collects the questions that can be displayed
     * We start at the initial question and get the next question based on the selected answer
     */
    @Watch("answers")
    private updateQuestions(): void {
        const questionsArray: Question[] = [questions[firstQuestionKey]];
        while (this.hasAnsweredLast(questionsArray) && this.lastAnswerHasNext(questionsArray)) {
            questionsArray.push(this.getNextQuestion(questionsArray[questionsArray.length - 1]));
        }
        this.updateAnswers(questionsArray);
        this.questions = questionsArray;
    }

    @Watch("answers")
    @Watch("questions")
    private checkComplete(): void {
        this.complete =
            this.hasAnsweredLast(this.questions) &&
            !this.lastAnswerHasNext(this.questions) &&
            this.hasAnsweredComment(this.questions);
    }

    private hasAnsweredLast(questionsArray: Question[]): boolean {
        return this.answers[questionsArray[questionsArray.length - 1].name] !== undefined;
    }

    private lastAnswerHasNext(questionsArray: Question[]): boolean {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        return lastQuestion.options[this.answers[lastQuestion.name]].next !== undefined;
    }

    private getNextQuestion(currentQuestion: Question): Question {
        const currentAnswer = this.answers[currentQuestion.name] as string;
        return questions[currentQuestion.options[currentAnswer].next as string];
    }

    private hasAnsweredComment(questionsArray: Question[]): boolean {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        if (!lastQuestion.comment) {
            return true;
        }
        const commentAnswer = this.answers[lastQuestion.comment.name];
        return commentAnswer && commentAnswer.trim() !== "";
    }

    /** this method removes those answers where the associated question has been removed */
    private updateAnswers(questionsArray: Question[]): void {
        const answersObject = { ...this.answers };
        Object.keys(answersObject).forEach((key) => {
            if (
                !questionsArray.find((question) => question.name === key) &&
                !questionsArray.find((question) => question.comment?.name === key)
            ) {
                delete answersObject[key];
            }
        });
        if (JSON.stringify(answersObject) !== JSON.stringify(this.answers)) {
            this.answers = answersObject;
        }
    }

    private onAuth(e: CustomEvent): void {
        if (this.isApp) {
            return;
        }
        this.authError = e.detail.error;
        if (this.authError !== null) {
            this.isAuthenticating = false;
            return;
        }
        createTicket()
            .then((token) => {
                if (!token) {
                    throw new Error("No TBDB token received");
                }
                this.tbdbToken = token;
            })
            .catch((error) => {
                // CORS errors are blocked by the browser and can't be shown to the user directly
                if (error.name === "TypeError" && error.message === "Failed to fetch") {
                    this.authError = "CORS error occurred requesting the TBDB token";
                    return;
                }
                this.authError = error || "An error occurred requesting the TBDB token";
            })
            .finally(() => {
                this.isAuthenticating = false;
            });
    }

    render() {
        const t = (key: TranslationKey, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const isLoading = this.loadingTranslations || this.isAuthenticating;

        if (!this.isApp && this.callId === "") {
            return <Host data-error="no callId provided" />;
        }

        if (this.config === null) {
            return <Host data-error="no config" />;
        }

        const silentAuth = !this.isApp && (
            <ux-u-silent-auth
                clientId={this.config?.authClientId}
                issuer={this.config?.authIssuer}
                onUxAuth={(e: CustomEvent) => this.onAuth(e)}
            />
        );

        if (isLoading) {
            return (
                <Host>
                    {silentAuth}
                    <div class={`${baseClass}__loader`}>
                        <ux-loader variant="spinner"></ux-loader>
                    </div>
                </Host>
            );
        }

        if (this.submitSuccess) {
            return (
                <Host class={baseClass}>
                    {silentAuth}
                    <div class={`${baseClass}__container`}>
                        <h1 class="ux-text-title ux-text-title--big ux-u-text-align--center">
                            {t("serviceCase.proactiveCaseClosure.dialogTitle")}
                        </h1>
                        <div class="ux-u-text-align--center">
                            <ux-button onClick={() => this.finish()} variant="primary">
                                {t("serviceCase.proactiveCaseClosure.dialog.buttonOk")}
                            </ux-button>
                        </div>
                    </div>
                </Host>
            );
        }

        if (!this.isApp && this.authError) {
            return (
                <Host class={baseClass}>
                    {silentAuth}
                    <div class={`${baseClass}__container`}>
                        <h1 class="ux-text-title ux-text-title--big ux-u-text-align--center">Error: Not authenticated</h1>
                        <p class="ux-text-normal ux-u-text-align--center ux-u-margin-vertical-20">{this.authError}</p>
                        <div class="ux-u-text-align--center">
                            <ux-button onClick={() => this.cancel()}>{t("button.cancel")}</ux-button>
                        </div>
                    </div>
                </Host>
            );
        }

        return (
            <Host class={baseClass}>
                {silentAuth}
                <div class={`${baseClass}__container`}>
                    {!this.isApp && (
                        <h1 class="ux-text-title ux-text-title--big ux-u-text-align--center">
                            {t("serviceCase.proactiveCaseClosure.title")}
                        </h1>
                    )}
                    <p class={`${baseClass}__introduction ux-text-normal`}>{t("serviceCase.proactiveCaseClosure.instructions")}</p>
                    {this.questions.map((question) => (
                        <div class={`${baseClass}__question-container ux-u-shadow ux-u-background-white ux-u-margin-vertical-20`}>
                            <QuestionRadio
                                t={t}
                                question={question}
                                answers={this.answers}
                                setAnswers={(answers) => {
                                    this.answers = answers;
                                }}
                                baseClass={baseClass}
                            />
                            {question.comment && (
                                <QuestionComment
                                    label={t(question.comment.labelKey as TranslationKey) as unknown as string}
                                    placeholder={t(question.comment.placeholderKey as TranslationKey) as unknown as string}
                                    name={question.comment.name}
                                    answers={this.answers}
                                    setAnswers={(answers) => {
                                        this.answers = answers;
                                    }}
                                />
                            )}
                        </div>
                    ))}
                    {this.submitError && <p class={`${baseClass}__error ux-text-normal`}>{this.submitError}</p>}
                </div>
                <div class={`${baseClass}__button-container`}>
                    {!this.isApp && (
                        <ux-button onClick={() => this.cancel()} class={`${baseClass}__cancel-button`}>
                            {t("button.cancel")}
                        </ux-button>
                    )}
                    <ux-button
                        onClick={() => this.submit()}
                        class={`${baseClass}__submit-button`}
                        variant="primary"
                        disabled={!this.complete || this.submitting}
                    >
                        {t("serviceCase.proactiveCaseClosure.question.submit")}
                    </ux-button>
                </div>
            </Host>
        );
    }
}

export interface ProactiveCaseClosureProps {
    /** SIS case ID */
    callId: string;
    /**
     * The locale to use for translations
     */
    locale: string;
    /**
     * Indicates if the component is used in an app context
     */
    isApp: boolean;
    /**
     * Environment string to decide which endpoints to use
     */
    env: string;
    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    legacyApi: boolean;
}
